import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Chip, Alert, CircularProgress, Button, Tooltip,
  Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tabs, Tab, Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Storage as StorageIcon,
  CalendarMonth as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  Bolt as BoltIcon,
  Analytics as AnalyticsIcon,
  ShowChart as ShowChartIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as AutoAwesomeIcon,
  TipsAndUpdates as TipsIcon,
  Speed as SpeedIcon,
  TrackChanges as TrackChangesIcon,
  GppBad as GppBadIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
  ReferenceLine, Legend, Area, ComposedChart,
} from 'recharts';
import { marketCorrectionAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════════════════
   Design tokens — match MarketRiskUnified
   ═══════════════════════════════════════════════════════════════════════ */
const shadow = {
  card: '0 1px 3px rgba(15,23,42,0.04), 0 4px 14px rgba(15,23,42,0.03)',
  hover: '0 4px 20px rgba(15,23,42,0.08), 0 1px 4px rgba(15,23,42,0.04)',
};
const radius = { sm: 8, md: 12, lg: 16 };

const cardSx = {
  bgcolor: '#fff',
  borderRadius: `${radius.lg}px`,
  boxShadow: shadow.card,
  border: '1px solid #f1f5f9',
  transition: 'box-shadow 0.2s, transform 0.15s',
};

const HORIZON_COLORS = {
  5: '#3b82f6', 10: '#8b5cf6', 20: '#f59e0b', 30: '#10b981',
};
const HORIZON_ICONS = {
  5: BoltIcon, 10: AnalyticsIcon, 20: ShowChartIcon, 30: VisibilityIcon,
};

/* ── helpers ── */
const pct = (v, d = 1) => `${(v * 100).toFixed(d)}%`;
const grade = (acc) => {
  if (acc >= 0.80) return { letter: 'A', color: '#10b981', label: 'excellent' };
  if (acc >= 0.65) return { letter: 'B', color: '#22c55e', label: 'good' };
  if (acc >= 0.55) return { letter: 'C', color: '#f59e0b', label: 'average' };
  if (acc >= 0.45) return { letter: 'D', color: '#f97316', label: 'poor' };
  return { letter: 'F', color: '#ef4444', label: 'bad' };
};
const labelColor = (l) =>
  l === 'STRONG' ? '#ef4444' : l === 'WEAK' ? '#f59e0b' : '#10b981';
const labelBg = (l) => alpha(labelColor(l), 0.08);

const reliabilityScore = (h) => {
  const o = h.optimal;
  if (!o) return Math.round((h.accuracy_binary * 0.6 + h.f1 * 0.4) * 100);
  return Math.round((o.accuracy * 0.6 + o.f1 * 0.4) * 100);
};
const reliabilityGrade = (s) => {
  if (s >= 75) return { color: '#10b981', label: 'reliabilityHigh' };
  if (s >= 55) return { color: '#f59e0b', label: 'reliabilityMedium' };
  return { color: '#ef4444', label: 'reliabilityLow' };
};

/* ── GradBar ── */
const GradBar = ({ value, color, height = 5, sx: extra = {} }) => (
  <Box
    sx={{
      position: 'relative',
      height,
      borderRadius: height,
      bgcolor: alpha(color, 0.08),
      overflow: 'hidden',
      ...extra,
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        width: `${Math.min(value, 100)}%`,
        borderRadius: height,
        background: `linear-gradient(90deg, ${alpha(color, 0.55)}, ${color})`,
        transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1)',
      }}
    />
  </Box>
);

/* ── SVG gauge with glow ── */
const ScoreGauge = ({ value, color, size = 130 }) => {
  const sw = 8;
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.min(value, 1));
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={alpha(color, 0.08)} strokeWidth={sw} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={sw}
          strokeDasharray={c} strokeDashoffset={off}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
        />
        {/* glow ring */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={sw + 6}
          strokeDasharray={c} strokeDashoffset={off}
          strokeLinecap="round" opacity={0.06}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   STAT TILE
   ═══════════════════════════════════════════════════════════════════════ */
const Tile = ({ icon: Icon, label, value, desc, color = '#6366f1' }) => (
  <Tooltip title={desc || ''} arrow placement="top">
    <Box
      sx={{
        ...cardSx,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: desc ? 'help' : 'default',
        '&:hover': { boxShadow: shadow.hover, transform: 'translateY(-1px)' },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: `${radius.md}px`,
          background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.7)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 3px 10px ${alpha(color, 0.25)}`,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: '#fff', fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 800,
            color: '#1e293b',
            lineHeight: 1.2,
            mt: 0.3,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  </Tooltip>
);

/* ═══════════════════════════════════════════════════════════════════════════
   HERO — gauge + horizon bars + stat tiles
   ═══════════════════════════════════════════════════════════════════════ */
const Hero = ({ horizons, totalEvaluated, t, dateRange, modelVersion }) => {
  const entries = Object.entries(horizons);
  const totalN = entries.reduce((s, [, v]) => s + v.n, 0);
  const wAcc =
    totalN > 0
      ? entries.reduce(
          (s, [, v]) =>
            s + (v.optimal ? v.optimal.accuracy : v.accuracy_binary) * v.n,
          0,
        ) / totalN
      : 0;
  const g = grade(wAcc);
  const totalCorrect = entries.reduce(
    (s, [, v]) =>
      v.optimal ? s + v.optimal.tp + v.optimal.tn : s + v.tp + v.tn,
    0,
  );

  const horizonBreakdown = entries
    .map(([h, d]) => ({
      h: Number(h),
      acc: d.optimal ? d.optimal.accuracy : d.accuracy_binary,
      score: reliabilityScore(d),
    }))
    .sort((a, b) => b.score - a.score);
  const bestH = horizonBreakdown[0]?.h;

  const avgP =
    totalN > 0
      ? entries.reduce(
          (s, [, v]) =>
            s + (v.optimal ? v.optimal.precision : v.precision) * v.n,
          0,
        ) / totalN
      : 0;
  const avgR =
    totalN > 0
      ? entries.reduce(
          (s, [, v]) => s + (v.optimal ? v.optimal.recall : v.recall) * v.n,
          0,
        ) / totalN
      : 0;
  const tFP = entries.reduce(
    (s, [, v]) => s + (v.optimal ? v.optimal.fp : v.fp),
    0,
  );
  const tTP = entries.reduce(
    (s, [, v]) => s + (v.optimal ? v.optimal.tp : v.tp),
    0,
  );
  const falseRate = tTP + tFP > 0 ? tFP / (tTP + tFP) : 0;
  const avgBrier =
    entries.length > 0
      ? entries.reduce((s, [, v]) => s + (v.brier_score || 0), 0) /
        entries.length
      : 0;
  const brierQ =
    avgBrier <= 0.15
      ? 'excellent'
      : avgBrier <= 0.25
        ? 'good'
        : avgBrier <= 0.35
          ? 'fair'
          : 'poor';

  return (
    <>
      {/* Gauge + Horizon bars */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
          gap: 3,
          mb: 2.5,
        }}
      >
        {/* Left — gauge */}
        <Box
          sx={{
            ...cardSx,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: `linear-gradient(145deg, ${alpha(g.color, 0.03)}, #fff)`,
          }}
        >
          <Box sx={{ position: 'relative', mb: 1 }}>
            <ScoreGauge value={wAcc} color={g.color} size={130} />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  color: g.color,
                  fontSize: '2.2rem',
                  lineHeight: 1,
                  letterSpacing: '-0.03em',
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {pct(wAcc, 1)}
              </Typography>
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  mt: 0.3,
                  letterSpacing: '0.05em',
                }}
              >
                {t(`modelAccuracy.grade_${g.label}`).toUpperCase()}
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: '#64748b',
              fontWeight: 500,
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            {t('modelAccuracy.heroMessage')}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#1e293b',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {totalCorrect}/{totalN} {t('modelAccuracy.correct').toLowerCase()}
          </Typography>
          {/* Meta chips */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mt: 1.5,
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {dateRange && (
              <Chip
                icon={<CalendarIcon sx={{ fontSize: 13 }} />}
                label={`${dateRange.from} → ${dateRange.to}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.62rem',
                  fontWeight: 500,
                  bgcolor: '#f8fafc',
                  color: '#94a3b8',
                  border: '1px solid #f1f5f9',
                  '& .MuiChip-icon': { color: '#94a3b8' },
                }}
              />
            )}
            {modelVersion && (
              <Chip
                label={modelVersion}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.62rem',
                  fontWeight: 500,
                  bgcolor: '#f8fafc',
                  color: '#94a3b8',
                  border: '1px solid #f1f5f9',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Right — horizon progress bars */}
        <Box sx={{ ...cardSx, p: 2.5, display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 2,
            }}
          >
            {t('modelAccuracy.heroPerHorizon')}
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {horizonBreakdown.map((hb) => {
              const c = HORIZON_COLORS[hb.h] || '#94a3b8';
              const HIcon = HORIZON_ICONS[hb.h] || AnalyticsIcon;
              const isBest = hb.h === bestH;
              return (
                <Box key={hb.h}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: `${radius.sm}px`,
                          bgcolor: alpha(c, 0.08),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <HIcon sx={{ fontSize: 15, color: c }} />
                      </Box>
                      <Typography
                        sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}
                      >
                        {hb.h} {t('modelAccuracy.days')}
                      </Typography>
                      {isBest && (
                        <Chip
                          label={t('modelAccuracy.bestHorizon')}
                          size="small"
                          icon={<TrophyIcon sx={{ fontSize: 12 }} />}
                          sx={{
                            height: 20,
                            fontSize: '0.58rem',
                            fontWeight: 700,
                            bgcolor: alpha(c, 0.08),
                            color: c,
                            border: `1px solid ${alpha(c, 0.15)}`,
                            '& .MuiChip-icon': { color: c },
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.92rem',
                        fontWeight: 800,
                        color: c,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {pct(hb.acc, 1)}
                    </Typography>
                  </Box>
                  <GradBar value={hb.acc * 100} color={c} height={6} />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Stat tiles row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2,1fr)',
            sm: 'repeat(3,1fr)',
            md: 'repeat(5,1fr)',
          },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Tile
          icon={StorageIcon}
          label={t('modelAccuracy.totalEvaluated')}
          value={totalEvaluated}
          color="#3b82f6"
        />
        <Tile
          icon={CheckCircleIcon}
          label={t('modelAccuracy.heroPrecision')}
          value={pct(avgP, 0)}
          desc={t('modelAccuracy.heroPrecisionDesc')}
          color="#10b981"
        />
        <Tile
          icon={TrackChangesIcon}
          label={t('modelAccuracy.heroDetectionRate')}
          value={pct(avgR, 0)}
          desc={t('modelAccuracy.heroRecallDesc')}
          color="#f59e0b"
        />
        <Tile
          icon={GppBadIcon}
          label={t('modelAccuracy.heroFalseAlarm')}
          value={pct(falseRate, 0)}
          desc={t('modelAccuracy.heroFalseAlarmDesc')}
          color={falseRate > 0.3 ? '#ef4444' : '#94a3b8'}
        />
        <Tile
          icon={TuneIcon}
          label={t('modelAccuracy.heroBrierScore')}
          value={avgBrier.toFixed(3)}
          desc={t(`modelAccuracy.brier_${brierQ}`)}
          color="#8b5cf6"
        />
      </Box>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   HORIZON COMPARISON CARDS
   ═══════════════════════════════════════════════════════════════════════ */
const HorizonCards = ({ horizons, t }) => {
  const sorted = Object.entries(horizons)
    .map(([h, d]) => ({
      h: Number(h),
      d,
      score: reliabilityScore(d),
      acc: d.optimal ? d.optimal.accuracy : d.accuracy_binary,
      correct: d.optimal ? d.optimal.tp + d.optimal.tn : d.tp + d.tn,
      missed: d.optimal ? d.optimal.fn : d.fn,
      falseAlarms: d.optimal ? d.optimal.fp : d.fp,
      recall: d.optimal ? d.optimal.recall : d.recall,
    }))
    .sort((a, b) => b.score - a.score);
  const bestH = sorted[0]?.h;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: '0.84rem',
          fontWeight: 700,
          color: '#1e293b',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
        }}
      >
        {t('modelAccuracy.horizonComparison')}
        <Tooltip title={t('modelAccuracy.horizonComparisonTip')} arrow>
          <InfoIcon sx={{ fontSize: 15, color: '#cbd5e1', cursor: 'help' }} />
        </Tooltip>
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2,1fr)',
            md: 'repeat(4,1fr)',
          },
          gap: 1.5,
        }}
      >
        {sorted.map(({ h, d, score, acc, correct, missed, falseAlarms, recall }, idx) => {
          const rg = reliabilityGrade(score);
          const c = HORIZON_COLORS[h] || '#94a3b8';
          const HIcon = HORIZON_ICONS[h] || AnalyticsIcon;
          const isBest = h === bestH;
          return (
            <Box
              key={h}
              sx={{
                ...cardSx,
                p: 0,
                position: 'relative',
                border: isBest
                  ? `1.5px solid ${alpha(c, 0.4)}`
                  : '1px solid #f1f5f9',
                '&:hover': { boxShadow: shadow.hover, transform: 'translateY(-1px)' },
              }}
            >
              {/* Top accent bar */}
              <Box
                sx={{
                  height: 3,
                  background: `linear-gradient(90deg, ${c}, ${alpha(c, 0.3)})`,
                }}
              />

              {isBest && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -9,
                    right: 14,
                    zIndex: 1,
                    px: 1,
                    py: 0.2,
                    borderRadius: `${radius.sm}px`,
                    background: `linear-gradient(135deg, ${c}, ${alpha(c, 0.8)})`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.3,
                    boxShadow: `0 2px 8px ${alpha(c, 0.3)}`,
                  }}
                >
                  <TrophyIcon sx={{ fontSize: 11, color: '#fff' }} />
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#fff' }}>
                    {t('modelAccuracy.bestHorizon')}
                  </Typography>
                </Box>
              )}

              <Box sx={{ p: 2 }}>
                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: `${radius.md}px`,
                        background: `linear-gradient(135deg, ${c}, ${alpha(c, 0.7)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 2px 8px ${alpha(c, 0.2)}`,
                      }}
                    >
                      <HIcon sx={{ color: '#fff', fontSize: 17 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.92rem',
                          fontWeight: 700,
                          color: '#1e293b',
                          lineHeight: 1.2,
                        }}
                      >
                        {h} {t('modelAccuracy.days')}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.2 }}
                      >
                        {t(`modelAccuracy.horizon_${h}_desc`)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8' }}>
                      #{idx + 1}
                    </Typography>
                  </Box>
                </Box>

                {/* Score */}
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {t('modelAccuracy.reliabilityScore')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography
                        sx={{
                          fontSize: '1.3rem',
                          fontWeight: 800,
                          color: rg.color,
                          lineHeight: 1,
                          fontFeatureSettings: '"tnum"',
                        }}
                      >
                        {score}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#cbd5e1' }}>
                        /100
                      </Typography>
                    </Box>
                  </Box>
                  <GradBar value={score} color={rg.color} height={6} />
                  <Typography
                    sx={{ fontSize: '0.6rem', fontWeight: 600, color: rg.color, mt: 0.4 }}
                  >
                    {t(`modelAccuracy.${rg.label}`)}
                  </Typography>
                </Box>

                {/* Quick metrics */}
                {[
                  {
                    icon: SpeedIcon,
                    label: t('modelAccuracy.correctPredictions'),
                    val: pct(acc, 0),
                    c: acc >= 0.65 ? '#10b981' : acc >= 0.5 ? '#f59e0b' : '#ef4444',
                  },
                  {
                    icon: TrackChangesIcon,
                    label: t('modelAccuracy.detectionRate'),
                    val: pct(recall, 0),
                    c: recall >= 0.5 ? '#10b981' : recall >= 0.3 ? '#f59e0b' : '#ef4444',
                  },
                  {
                    icon: GppBadIcon,
                    label: t('modelAccuracy.falseAlarms'),
                    val: String(falseAlarms),
                    c: '#94a3b8',
                  },
                ].map((m, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <m.icon sx={{ fontSize: 14, color: '#cbd5e1' }} />
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {m.label}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: m.c,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {m.val}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderTop: '1px solid #f1f5f9',
                  bgcolor: '#fafbfc',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  {t('modelAccuracy.correctOf', { correct, total: d.n })}
                  {missed > 0 && (
                    <Typography
                      component="span"
                      sx={{ fontSize: '0.68rem', color: '#f59e0b', ml: 0.5 }}
                    >
                      · {t('modelAccuracy.missed', { count: missed })}
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   TECHNICAL DETAILS
   ═══════════════════════════════════════════════════════════════════════ */
const MetricBar = ({ label, desc, value, format, colorFn, best }) => {
  const formatted = format === 'brier' ? value.toFixed(3) : pct(value, 1);
  const color = colorFn(value);
  const bar =
    format === 'brier'
      ? Math.max(0, (1 - value / 0.5) * 100)
      : Math.min(value * 100, 100);
  return (
    <Box sx={{ py: 0.7 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.3,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}>
            {label}
          </Typography>
          {best && <TrophyIcon sx={{ fontSize: 11, color: '#f59e0b' }} />}
        </Box>
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color,
            fontFamily: 'monospace',
            flexShrink: 0,
          }}
        >
          {formatted}
        </Typography>
      </Box>
      <GradBar value={bar} color={color} height={4} />
      {desc && (
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.3, lineHeight: 1.3 }}>
          {desc}
        </Typography>
      )}
    </Box>
  );
};

const ConfusionGrid = ({ tp, fp, fn, tn, t: tr }) => {
  const total = tp + fp + fn + tn;
  const Cell = ({ label, value, color, desc }) => (
    <Tooltip title={desc} arrow>
      <Box
        sx={{
          flex: 1,
          py: 0.8,
          borderRadius: `${radius.sm}px`,
          textAlign: 'center',
          bgcolor: alpha(color, 0.04),
          border: `1px solid ${alpha(color, 0.1)}`,
          transition: 'all 0.15s',
          '&:hover': {
            bgcolor: alpha(color, 0.08),
            borderColor: alpha(color, 0.2),
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '0.55rem',
            color: '#94a3b8',
            fontWeight: 600,
            letterSpacing: '0.03em',
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color, lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Cell
          label={tr('modelAccuracy.tp_short')}
          value={tp}
          color="#10b981"
          desc={tr('modelAccuracy.tp_desc')}
        />
        <Cell
          label={tr('modelAccuracy.fp_short')}
          value={fp}
          color="#ef4444"
          desc={tr('modelAccuracy.fp_desc')}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Cell
          label={tr('modelAccuracy.fn_short')}
          value={fn}
          color="#f59e0b"
          desc={tr('modelAccuracy.fn_desc')}
        />
        <Cell
          label={tr('modelAccuracy.tn_short')}
          value={tn}
          color="#3b82f6"
          desc={tr('modelAccuracy.tn_desc')}
        />
      </Box>
      <Typography
        sx={{ fontSize: '0.58rem', color: '#94a3b8', textAlign: 'center', mt: 0.2 }}
      >
        {tr('modelAccuracy.totalSamples')}: {total}
      </Typography>
    </Box>
  );
};

const TechSection = ({ horizons, t }) => {
  const sorted = Object.entries(horizons).sort(
    ([a], [b]) => Number(a) - Number(b),
  );
  const bestFor = (key, useOpt = false) => {
    let best = null;
    let bestVal = -Infinity;
    sorted.forEach(([h, s]) => {
      const v = useOpt && s.optimal ? s.optimal[key] : s[key];
      if (v != null && v > bestVal) {
        bestVal = v;
        best = h;
      }
    });
    return best;
  };
  const bestBrier = (() => {
    let best = null;
    let bestVal = Infinity;
    sorted.forEach(([h, s]) => {
      if (s.brier_score < bestVal) {
        bestVal = s.brier_score;
        best = h;
      }
    });
    return best;
  })();
  const cA = (v) => (v >= 0.65 ? '#10b981' : v >= 0.55 ? '#f59e0b' : '#ef4444');
  const cF = (v) => (v >= 0.5 ? '#10b981' : v >= 0.25 ? '#f59e0b' : '#ef4444');
  const cP = (v) => (v >= 0.6 ? '#10b981' : v >= 0.3 ? '#f59e0b' : '#ef4444');
  const cR = (v) => (v >= 0.5 ? '#10b981' : v >= 0.2 ? '#f59e0b' : '#ef4444');
  const cB = (v) => (v <= 0.2 ? '#10b981' : v <= 0.3 ? '#f59e0b' : '#ef4444');

  return (
    <Box sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2,1fr)',
            md: 'repeat(4,1fr)',
          },
          gap: 2,
        }}
      >
        {sorted.map(([h, s]) => {
          const c = HORIZON_COLORS[Number(h)] || '#94a3b8';
          const HIcon = HORIZON_ICONS[Number(h)] || AnalyticsIcon;
          const o = s.optimal;
          const gap = s.calibration_gap;
          const absGap = Math.abs(gap);
          const gapC =
            absGap <= 0.05 ? '#10b981' : absGap <= 0.12 ? '#f59e0b' : '#ef4444';
          return (
            <Box
              key={h}
              sx={{
                bgcolor: '#fff',
                borderRadius: `${radius.md}px`,
                border: `1px solid ${alpha(c, 0.15)}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  bgcolor: alpha(c, 0.03),
                  borderBottom: `1px solid ${alpha(c, 0.08)}`,
                }}
              >
                <HIcon sx={{ fontSize: 16, color: c }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: c }}>
                  {h}d
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                  (n={s.n})
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.58rem',
                    fontWeight: 700,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    mb: 0.5,
                  }}
                >
                  {t('modelAccuracy.techStandard50')}
                </Typography>
                <MetricBar
                  label={t('modelAccuracy.binaryAccuracy')}
                  desc={t('modelAccuracy.desc_accuracy')}
                  value={s.accuracy_binary}
                  colorFn={cA}
                  best={bestFor('accuracy_binary') === h}
                />
                <MetricBar
                  label={t('modelAccuracy.precision')}
                  desc={t('modelAccuracy.desc_precision')}
                  value={s.precision}
                  colorFn={cP}
                  best={bestFor('precision') === h}
                />
                <MetricBar
                  label={t('modelAccuracy.recall')}
                  desc={t('modelAccuracy.desc_recall')}
                  value={s.recall}
                  colorFn={cR}
                  best={bestFor('recall') === h}
                />
                <MetricBar
                  label={t('modelAccuracy.f1Score')}
                  desc={t('modelAccuracy.desc_f1')}
                  value={s.f1}
                  colorFn={cF}
                  best={bestFor('f1') === h}
                />
                <MetricBar
                  label={t('modelAccuracy.brierScore')}
                  desc={t('modelAccuracy.desc_brier')}
                  value={s.brier_score}
                  format="brier"
                  colorFn={cB}
                  best={bestBrier === h}
                />

                {/* Calibration */}
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9' }}>
                  <Typography
                    sx={{
                      fontSize: '0.58rem',
                      fontWeight: 700,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      mb: 0.5,
                    }}
                  >
                    {t('modelAccuracy.calibration')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                    <Box sx={{ flex: 1 }}>
                      {[
                        {
                          k: t('modelAccuracy.predicted'),
                          v: pct(s.avg_predicted_p, 1),
                        },
                        {
                          k: t('modelAccuracy.actual'),
                          v: pct(s.actual_correction_rate, 1),
                        },
                      ].map(({ k, v }) => (
                        <Box
                          key={k}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.2,
                          }}
                        >
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {k}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              color: '#1e293b',
                            }}
                          >
                            {v}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Chip
                      label={`${gap > 0 ? '+' : ''}${pct(gap, 1)}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        bgcolor: alpha(gapC, 0.08),
                        color: gapC,
                        border: `1px solid ${alpha(gapC, 0.15)}`,
                      }}
                    />
                  </Box>
                </Box>

                {/* Confusion matrix */}
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9' }}>
                  <Typography
                    sx={{
                      fontSize: '0.58rem',
                      fontWeight: 700,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      mb: 0.7,
                    }}
                  >
                    {t('modelAccuracy.confusion')}
                  </Typography>
                  <ConfusionGrid tp={s.tp} fp={s.fp} fn={s.fn} tn={s.tn} t={t} />
                </Box>

                {/* Optimal threshold */}
                {o && (
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `2px solid ${alpha('#6366f1', 0.08)}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        mb: 0.7,
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 13, color: '#6366f1' }} />
                      <Typography
                        sx={{
                          fontSize: '0.58rem',
                          fontWeight: 700,
                          color: '#6366f1',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {t('modelAccuracy.optimalThreshold')}
                      </Typography>
                      <Chip
                        label={`${(o.threshold * 100).toFixed(0)}%`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.58rem',
                          fontWeight: 700,
                          bgcolor: alpha('#6366f1', 0.08),
                          color: '#6366f1',
                          ml: 'auto',
                        }}
                      />
                    </Box>
                    <MetricBar
                      label={t('modelAccuracy.binaryAccuracy')}
                      value={o.accuracy}
                      colorFn={cA}
                      best={bestFor('accuracy', true) === h}
                    />
                    <MetricBar
                      label={t('modelAccuracy.precision')}
                      value={o.precision}
                      colorFn={cP}
                      best={bestFor('precision', true) === h}
                    />
                    <MetricBar
                      label={t('modelAccuracy.recall')}
                      value={o.recall}
                      colorFn={cR}
                      best={bestFor('recall', true) === h}
                    />
                    <MetricBar
                      label={t('modelAccuracy.f1Score')}
                      value={o.f1}
                      colorFn={cF}
                      best={bestFor('f1', true) === h}
                    />
                    <Box sx={{ mt: 0.7 }}>
                      <ConfusionGrid tp={o.tp} fp={o.fp} fn={o.fn} tn={o.tn} t={t} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CHARTS
   ═══════════════════════════════════════════════════════════════════════ */
const CalibrationChart = ({ horizons, t, selectedHorizon }) => {
  const data = Object.entries(horizons)
    .filter(([h]) => !selectedHorizon || Number(h) === selectedHorizon)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([h, s]) => ({
      name: `${h}d`,
      predicted: +(s.avg_predicted_p * 100).toFixed(1),
      actual: +(s.actual_correction_rate * 100).toFixed(1),
    }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} barGap={6} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12, fill: '#94a3b8' }}
          axisLine={{ stroke: '#f1f5f9' }}
          tickLine={false}
        />
        <YAxis
          unit="%"
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          axisLine={false}
          tickLine={false}
        />
        <RechartsTooltip
          formatter={(v) => `${v}%`}
          contentStyle={{
            borderRadius: 12,
            border: '1px solid #f1f5f9',
            boxShadow: shadow.card,
            fontSize: 12,
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar
          dataKey="predicted"
          name={t('modelAccuracy.predicted')}
          fill="#3b82f6"
          radius={[4, 4, 0, 0]}
          barSize={28}
        />
        <Bar
          dataKey="actual"
          name={t('modelAccuracy.actual')}
          fill="#10b981"
          radius={[4, 4, 0, 0]}
          barSize={28}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const Timeline = ({ log, selectedHorizon, optimalThresholds }) => {
  const data = useMemo(() => {
    const m = {};
    log.forEach((e) => {
      if (selectedHorizon && e.horizon_days !== selectedHorizon) return;
      if (!m[e.calc_date]) m[e.calc_date] = { date: e.calc_date };
      m[e.calc_date][`p${e.horizon_days}`] = +(e.p_corr * 100).toFixed(1);
    });
    return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
  }, [log, selectedHorizon]);
  const hs = selectedHorizon
    ? [selectedHorizon]
    : [...new Set(log.map((e) => e.horizon_days))].sort((a, b) => a - b);
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <defs>
          {hs.map((h) => (
            <linearGradient key={h} id={`g${h}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={HORIZON_COLORS[h]} stopOpacity={0.15} />
              <stop offset="100%" stopColor={HORIZON_COLORS[h]} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickFormatter={(v) => v.slice(5)}
          axisLine={{ stroke: '#f1f5f9' }}
          tickLine={false}
        />
        <YAxis
          unit="%"
          domain={[0, 80]}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          axisLine={false}
          tickLine={false}
        />
        <RechartsTooltip
          contentStyle={{
            borderRadius: 12,
            border: '1px solid #f1f5f9',
            boxShadow: shadow.card,
            fontSize: 12,
          }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <ReferenceLine y={50} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.4} />
        {selectedHorizon &&
          optimalThresholds?.[selectedHorizon] &&
          optimalThresholds[selectedHorizon] !== 0.5 && (
            <ReferenceLine
              y={optimalThresholds[selectedHorizon] * 100}
              stroke="#6366f1"
              strokeDasharray="6 3"
              strokeOpacity={0.6}
              strokeWidth={1.5}
            />
          )}
        {hs.map((h) => (
          <Area
            key={h}
            type="monotone"
            dataKey={`p${h}`}
            name={`${h}d`}
            stroke={HORIZON_COLORS[h]}
            fill={`url(#g${h})`}
            strokeWidth={1.5}
            dot={false}
            connectNulls
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PREDICTION LOG TABLE
   ═══════════════════════════════════════════════════════════════════════ */
const PredictionLog = ({ log, t, selectedHorizon }) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;
  const filtered = useMemo(() => {
    const f = selectedHorizon
      ? log.filter((r) => r.horizon_days === selectedHorizon)
      : log;
    return [...f].sort(
      (a, b) =>
        b.calc_date.localeCompare(a.calc_date) || a.horizon_days - b.horizon_days,
    );
  }, [log, selectedHorizon]);
  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  useEffect(() => setPage(0), [selectedHorizon]);
  const correctCount = filtered.filter(
    (r) => (r.opt_correct != null ? r.opt_correct : r.binary_correct),
  ).length;
  const correctRate = filtered.length > 0 ? correctCount / filtered.length : 0;

  const cols = [
    { label: t('modelAccuracy.date'), align: 'left' },
    { label: t('modelAccuracy.horizonShort'), align: 'center' },
    { label: t('modelAccuracy.pCorr'), align: 'center' },
    { label: t('modelAccuracy.predictedLabel'), align: 'center' },
    { label: t('modelAccuracy.actualLabel'), align: 'center' },
    { label: t('modelAccuracy.actualDD'), align: 'center' },
    { label: t('modelAccuracy.correct'), align: 'center', width: 48 },
  ];

  return (
    <Box sx={{ ...cardSx, mb: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f1f5f9',
          bgcolor: '#fafbfc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: '#1e293b' }}>
            {t('modelAccuracy.predictionLog')}
          </Typography>
          <Chip
            label={filtered.length}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 600,
              bgcolor: '#f1f5f9',
              color: '#64748b',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
          <Typography
            sx={{
              fontSize: '0.72rem',
              color: '#64748b',
              fontWeight: 600,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {correctCount}/{filtered.length} ({pct(correctRate, 0)})
          </Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {cols.map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    py: 1,
                    letterSpacing: '0.04em',
                    bgcolor: '#fafbfc',
                    borderBottom: '1px solid #f1f5f9',
                    ...(col.width && { width: col.width }),
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row, i) => {
              const ok =
                row.opt_correct != null ? row.opt_correct : row.binary_correct;
              return (
                <TableRow
                  key={`${row.calc_date}-${row.horizon_days}-${i}`}
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    transition: 'background-color 0.1s',
                    '& td': { borderBottom: '1px solid #f8fafc' },
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.7,
                      fontFamily: 'monospace',
                      fontWeight: 500,
                      color: '#1e293b',
                    }}
                  >
                    {row.calc_date}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.7 }}>
                    <Chip
                      size="small"
                      label={`${row.horizon_days}d`}
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        minWidth: 32,
                        bgcolor: alpha(
                          HORIZON_COLORS[row.horizon_days] || '#888',
                          0.08,
                        ),
                        color: HORIZON_COLORS[row.horizon_days] || '#888',
                        border: `1px solid ${alpha(
                          HORIZON_COLORS[row.horizon_days] || '#888',
                          0.12,
                        )}`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.7 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: '#f1f5f9',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(row.p_corr * 100, 100)}%`,
                            height: '100%',
                            borderRadius: 2,
                            bgcolor: row.p_corr >= 0.5 ? '#ef4444' : '#10b981',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          color: row.p_corr >= 0.5 ? '#ef4444' : '#10b981',
                          fontFamily: 'monospace',
                        }}
                      >
                        {pct(row.p_corr, 1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.7 }}>
                    <Chip
                      label={t(`modelAccuracy.${row.predicted_label.toLowerCase()}`)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: labelBg(row.predicted_label),
                        color: labelColor(row.predicted_label),
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.7 }}>
                    <Chip
                      label={t(`modelAccuracy.${row.actual_label.toLowerCase()}`)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: labelBg(row.actual_label),
                        color: labelColor(row.actual_label),
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.7 }}>
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontFamily: 'monospace',
                        fontWeight: row.actual_dd < -0.03 ? 600 : 400,
                        color: row.actual_dd < -0.03 ? '#ef4444' : '#94a3b8',
                      }}
                    >
                      {row.actual_dd != null ? pct(row.actual_dd, 2) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.7 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        mx: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: ok
                          ? alpha('#10b981', 0.08)
                          : alpha('#ef4444', 0.08),
                      }}
                    >
                      {ok ? (
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
            py: 1.2,
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            sx={{
              minWidth: 28,
              fontSize: '0.72rem',
              borderRadius: `${radius.sm}px`,
              color: '#64748b',
            }}
          >
            ‹
          </Button>
          <Typography
            sx={{
              fontSize: '0.72rem',
              color: '#94a3b8',
              fontWeight: 600,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {page + 1} / {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            sx={{
              minWidth: 28,
              fontSize: '0.72rem',
              borderRadius: `${radius.sm}px`,
              color: '#64748b',
            }}
          >
            ›
          </Button>
        </Box>
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   LOADING SKELETON
   ═══════════════════════════════════════════════════════════════════════ */
const LoadingSkeleton = () => (
  <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
    <Skeleton variant="rounded" height={44} width={320} sx={{ borderRadius: 2, mb: 3 }} />
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
        gap: 3,
        mb: 2.5,
      }}
    >
      <Skeleton variant="rounded" height={320} sx={{ borderRadius: `${radius.lg}px` }} />
      <Skeleton variant="rounded" height={320} sx={{ borderRadius: `${radius.lg}px` }} />
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(5,1fr)' },
        gap: 1.5,
        mb: 3,
      }}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={72}
          sx={{ borderRadius: `${radius.lg}px` }}
        />
      ))}
    </Box>
    <Skeleton variant="rounded" height={20} width={160} sx={{ mb: 2, borderRadius: 1 }} />
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' },
        gap: 1.5,
        mb: 3,
      }}
    >
      {[1, 2, 3, 4].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={300}
          sx={{ borderRadius: `${radius.lg}px` }}
        />
      ))}
    </Box>
    <Skeleton variant="rounded" height={36} width={260} sx={{ mb: 2, borderRadius: 1 }} />
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2fr 3fr' },
        gap: 2,
        mb: 3,
      }}
    >
      <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${radius.lg}px` }} />
      <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${radius.lg}px` }} />
    </Box>
    <Skeleton variant="rounded" height={360} sx={{ borderRadius: `${radius.lg}px` }} />
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════ */
const ModelAccuracyPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);
  const [horizonFilter, setHorizonFilter] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [showTech, setShowTech] = useState(false);

  const fetchAccuracy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketCorrectionAPI.getAccuracy();
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccuracy();
  }, [fetchAccuracy]);

  const horizons = data?.horizons || {};
  const log = useMemo(() => data?.prediction_log || [], [data?.prediction_log]);
  const totalEvaluated = data?.total_evaluated || 0;
  const optimalThresholds = data?.optimal_thresholds || {};
  const hasData = totalEvaluated > 0;
  const selectedHorizon = horizonFilter || null;
  const horizonKeys = Object.keys(horizons)
    .map(Number)
    .sort((a, b) => a - b);

  const dateRange = useMemo(() => {
    if (!log.length) return null;
    const dates = log.map((e) => e.calc_date).sort();
    return { from: dates[0], to: dates[dates.length - 1] };
  }, [log]);

  const modelVersion = useMemo(() => {
    if (!log.length) return null;
    const vs = {};
    log.forEach((e) => {
      if (e.model_version) vs[e.model_version] = (vs[e.model_version] || 0) + 1;
    });
    const s = Object.entries(vs).sort((a, b) => b[1] - a[1]);
    if (!s.length) return null;
    const m = s[0][0].match(/corrprob_(v\d+)_(h\d+)/);
    return m ? `${m[1]} ${m[2]}` : s[0][0];
  }, [log]);

  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillMsg(null);
    try {
      const res = await marketCorrectionAPI.backfillPredictions();
      setBackfillMsg(
        t('modelAccuracy.backfillSuccess', { processed: res.data.processed }),
      );
      await fetchAccuracy();
    } catch (err) {
      setBackfillMsg(`Error: ${err.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error)
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Alert
          severity="error"
          sx={{ borderRadius: `${radius.md}px` }}
          action={<Button onClick={fetchAccuracy}>{t('modelAccuracy.refresh')}</Button>}
        >
          {error}
        </Alert>
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: `${radius.md}px`,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 3px 10px ${alpha('#6366f1', 0.25)}`,
            }}
          >
            <AssessmentIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: '#0f172a',
                fontSize: '1.2rem',
                letterSpacing: '-0.01em',
              }}
            >
              {t('modelAccuracy.title')}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('modelAccuracy.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              backfilling ? (
                <CircularProgress size={14} />
              ) : (
                <StorageIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={handleBackfill}
            disabled={backfilling}
            sx={{
              borderRadius: '10px',
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                borderColor: '#6366f1',
                color: '#6366f1',
                bgcolor: alpha('#6366f1', 0.04),
              },
            }}
          >
            {backfilling
              ? t('modelAccuracy.backfilling')
              : t('modelAccuracy.backfill')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchAccuracy}
            sx={{
              borderRadius: '10px',
              borderColor: '#e2e8f0',
              color: '#64748b',
              minWidth: 36,
              px: 1,
              '&:hover': {
                borderColor: '#6366f1',
                color: '#6366f1',
                bgcolor: alpha('#6366f1', 0.04),
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {backfillMsg && (
        <Alert
          severity={backfillMsg.startsWith('Error') ? 'error' : 'success'}
          onClose={() => setBackfillMsg(null)}
          sx={{ mb: 2, borderRadius: `${radius.md}px`, fontSize: '0.78rem' }}
        >
          {backfillMsg}
        </Alert>
      )}

      {!hasData ? (
        <Box sx={{ ...cardSx, p: 5, textAlign: 'center' }}>
          <InfoIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: '#64748b' }}>{t('modelAccuracy.noData')}</Typography>
        </Box>
      ) : (
        <>
          <Hero
            horizons={horizons}
            totalEvaluated={totalEvaluated}
            t={t}
            dateRange={dateRange}
            modelVersion={modelVersion}
          />

          {/* How it works — collapsible */}
          <Box sx={{ ...cardSx, mb: 3, overflow: 'hidden' }}>
            <Box
              onClick={() => setShowInfo((v) => !v)}
              sx={{
                px: 2.5,
                py: 1.2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                bgcolor: '#fafbfc',
                '&:hover': { bgcolor: '#f8fafc' },
                transition: 'background-color 0.15s',
              }}
            >
              <InfoIcon sx={{ fontSize: 16, color: '#6366f1' }} />
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#6366f1',
                  flex: 1,
                }}
              >
                {t('modelAccuracy.infoTitle')}
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: '#94a3b8',
                  transform: showInfo ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
            <Collapse in={showInfo}>
              <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #f1f5f9' }}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    color: '#64748b',
                    lineHeight: 1.7,
                    mb: 1,
                  }}
                >
                  {t('modelAccuracy.infoDescription')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.8,
                    bgcolor: alpha('#f59e0b', 0.04),
                    p: 1.5,
                    borderRadius: `${radius.sm}px`,
                    border: `1px solid ${alpha('#f59e0b', 0.1)}`,
                  }}
                >
                  <TipsIcon
                    sx={{ fontSize: 16, color: '#f59e0b', mt: 0.1, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.7 }}
                  >
                    {t('modelAccuracy.infoOptimal')}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </Box>

          <HorizonCards horizons={horizons} t={t} />

          {/* Horizon filter tabs */}
          <Box sx={{ mb: 2.5 }}>
            <Tabs
              value={horizonFilter}
              onChange={(_, v) => setHorizonFilter(v)}
              sx={{
                minHeight: 36,
                '& .MuiTab-root': {
                  minHeight: 36,
                  py: 0.5,
                  px: 2,
                  fontSize: '0.72rem',
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  mr: 0.5,
                  color: '#64748b',
                  '&.Mui-selected': { fontWeight: 700 },
                },
                '& .MuiTabs-indicator': {
                  height: 2.5,
                  borderRadius: 2,
                  bgcolor: '#6366f1',
                },
              }}
            >
              <Tab label={t('modelAccuracy.all')} value={0} />
              {horizonKeys.map((h) => (
                <Tab
                  key={h}
                  label={`${h}d`}
                  value={h}
                  sx={{ '&.Mui-selected': { color: HORIZON_COLORS[h] } }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Charts row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 3fr' },
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ ...cardSx, p: 2.5 }}>
              <Typography
                sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', mb: 1.5 }}
              >
                {t('modelAccuracy.calibrationChart')}
              </Typography>
              <CalibrationChart
                horizons={horizons}
                t={t}
                selectedHorizon={selectedHorizon}
              />
            </Box>
            <Box sx={{ ...cardSx, p: 2.5 }}>
              <Typography
                sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', mb: 1.5 }}
              >
                {t('modelAccuracy.timeline')}
              </Typography>
              <Timeline
                log={log}
                selectedHorizon={selectedHorizon}
                optimalThresholds={optimalThresholds}
              />
            </Box>
          </Box>

          <PredictionLog log={log} t={t} selectedHorizon={selectedHorizon} />

          {/* Technical Details — collapsible */}
          <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <Box
              onClick={() => setShowTech((v) => !v)}
              sx={{
                px: 2.5,
                py: 1.2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                bgcolor: '#fafbfc',
                '&:hover': { bgcolor: '#f8fafc' },
                transition: 'background-color 0.15s',
              }}
            >
              <TuneIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#64748b',
                  flex: 1,
                }}
              >
                {t('modelAccuracy.technicalDetails')}
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: '#94a3b8',
                  transform: showTech ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
            <Collapse in={showTech}>
              <Box sx={{ borderTop: '1px solid #f1f5f9' }}>
                <TechSection horizons={horizons} t={t} />
              </Box>
            </Collapse>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ModelAccuracyPage;
